import { canAccessModule, canDo, canOpenScreen, MODULES_BY_KEY, menuKey, type ModuleKey, type PermissionMap } from '@ruby/shared';
import { createContext, useContext } from 'react';
import { useSession } from './session';

const EMPTY: PermissionMap = {};
/** Actions still allowed on a page opened "view only". */
const READ_ACTIONS = new Set(['view', 'print', 'export']);

/** A page opened "view only" (?mode=view): every check for anything but view / print / export says no. */
export const ViewOnlyContext = createContext(false);

export function usePermissions() {
  const perms: PermissionMap = useSession((s) => s.boot?.permissions) ?? EMPTY;
  const viewOnly = useContext(ViewOnlyContext);
  return {
    perms,
    viewOnly,
    can: (module: string, screen: string, action: string) => (!viewOnly || READ_ACTIONS.has(action)) && canDo(perms, module, screen, action),
    canModule: (module: string) => canAccessModule(perms, module),
    canMenu: (module: string, menu: string) => canAccessModule(perms, module) && !!perms[menuKey(module, menu)],
  };
}

/** Screens of a module that the user may open (menu + view), grouped by menu. */
export function visibleMenus(perms: PermissionMap, module: ModuleKey) {
  const def = MODULES_BY_KEY[module];
  return def.menus
    .filter((m) => perms[menuKey(module, m.key)])
    .map((m) => ({ ...m, screens: m.screens.filter((s) => canOpenScreen(perms, module, s.key)) }))
    .filter((m) => m.screens.length > 0);
}
